/**
 * Pure helpers for deriving the data props that StrategyCumulativeChart,
 * StrategyCalendarView, and KPI summaries expect from a Vector Results
 * strategy-sheet response (the 2D `rawRows` array). Mirrors the logic that
 * lives inside StrategyDetailsView so dashboard widgets can render the same
 * views without pulling in that page's heavy local state.
 */

const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const EMPTY_WEEK_DAYS = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0 };

function parseNumber(value) {
  if (value === undefined || value === null) return 0;
  const cleaned = value.toString().replace(/[$,]/g, '').trim();
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}

function parseDateValue(value) {
  if (!value) return 0;
  const parts = value.split('.');
  if (parts.length !== 3) return 0;
  const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
  if (Number.isNaN(mm) || Number.isNaN(dd) || Number.isNaN(yyyy)) return 0;
  return yyyy * 10000 + mm * 100 + dd;
}

function ordinalSuffix(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

function formatWeekLabel(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('.');
  if (parts.length !== 3) return dateStr;
  const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
  if (Number.isNaN(mm) || Number.isNaN(dd) || Number.isNaN(yyyy)) return dateStr;
  const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
  const month = months[mm - 1] || '';
  return `Week of ${month} ${ordinalSuffix(dd)}, ${yyyy}`;
}

function mondayDateStringForCalendarDay(year, month0, day) {
  const d = new Date(year, month0, day);
  const dow = d.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + mondayOffset);
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const yyyy = d.getFullYear();
  return `${String(mm).padStart(2, '0')}.${String(dd).padStart(2, '0')}.${yyyy}`;
}

function compareMMDDYYYYWeekAsc(a, b) {
  const [mma, dda, yya] = a.split('.').map(Number);
  const [mmb, ddb, yyb] = b.split('.').map(Number);
  return (yya * 10000 + mma * 100 + dda) - (yyb * 10000 + mmb * 100 + ddb);
}

function mondayKeysOverlappingMonth(year, month0) {
  const lastDay = new Date(year, month0 + 1, 0).getDate();
  const keys = new Set();
  for (let day = 1; day <= lastDay; day++) {
    const d = new Date(year, month0, day);
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue;
    keys.add(mondayDateStringForCalendarDay(year, month0, day));
  }
  return [...keys].sort(compareMMDDYYYYWeekAsc);
}

/** Returns the column indexes for Mon..Fri using header row 1 (with 1-5 fallbacks). */
function resolveDayColumns(rawRows) {
  const headerRow = rawRows?.[1] || [];
  const find = (label) => headerRow.findIndex((c) => String(c || '').toLowerCase() === label);
  const idxMonday = find('monday');
  const idxTuesday = find('tuesday');
  const idxWednesday = find('wednesday');
  const idxThursday = find('thursday');
  const idxFriday = find('friday');
  return {
    Mon: idxMonday >= 0 ? idxMonday : 1,
    Tue: idxTuesday >= 0 ? idxTuesday : 2,
    Wed: idxWednesday >= 0 ? idxWednesday : 3,
    Thu: idxThursday >= 0 ? idxThursday : 4,
    Fri: idxFriday >= 0 ? idxFriday : 5,
  };
}

/** Convert raw spreadsheet rows into [{ date: 'MM.DD.YYYY', days: {Mon..Fri}, total }]. */
export function extractWeeklyData(rawRows) {
  if (!Array.isArray(rawRows) || rawRows.length < 3) return [];
  const cols = resolveDayColumns(rawRows);
  const dataRows = rawRows.slice(2);
  return dataRows
    .map((row) => {
      const date = row?.[0];
      const monday = parseNumber(row?.[cols.Mon]);
      const tuesday = parseNumber(row?.[cols.Tue]);
      const wednesday = parseNumber(row?.[cols.Wed]);
      const thursday = parseNumber(row?.[cols.Thu]);
      const friday = parseNumber(row?.[cols.Fri]);
      const total = monday + tuesday + wednesday + thursday + friday;
      return {
        date,
        days: { Mon: monday, Tue: tuesday, Wed: wednesday, Thu: thursday, Fri: friday },
        total,
      };
    })
    .filter((w) => w.date)
    .sort((a, b) => parseDateValue(b.date) - parseDateValue(a.date));
}

/** Build YYYY-MM-DD → { value, trades: 1 } daily map (skips zero-value days). */
export function buildDailyDataMap(weeklyData) {
  const map = new Map();
  (weeklyData || []).forEach((week) => {
    const parts = (week?.date || '').split('.');
    if (parts.length !== 3) return;
    const mm = parseInt(parts[0], 10);
    const dd = parseInt(parts[1], 10);
    const yyyy = parseInt(parts[2], 10);
    if (Number.isNaN(mm) || Number.isNaN(dd) || Number.isNaN(yyyy)) return;
    DAY_ORDER.forEach((day, idx) => {
      const d = new Date(yyyy, mm - 1, dd + idx);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const value = week.days?.[day] ?? 0;
      if (value !== 0) {
        map.set(key, { value, trades: 1 });
      }
    });
  });
  return map;
}

function isMicroSheet(sheetName) {
  return String(sheetName || '').toLowerCase().includes('micro');
}

/** Currency formatter that mirrors the Vector Results contract scaling rules. */
export function makeCurrencyFormatters({ contractType = 'NQ', sheetName = '', contractSize = 1 } = {}) {
  const isMicro = isMicroSheet(sheetName);
  const showDecimals = contractType === 'MNQ' && !isMicro;

  const formatRaw = (val) => {
    if (val === undefined || val === null || Number.isNaN(val)) return '$0';
    let num = Number(val) * contractSize;
    if (contractType === 'MNQ' && !isMicro) num /= 10;
    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    const formatted = showDecimals
      ? abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return `${sign}$${formatted}`;
  };

  const formatScaledOnly = (val) => {
    const num = Number(val) || 0;
    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    const formatted = showDecimals
      ? abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return `${sign}$${formatted}`;
  };

  const formatAxis = (val) => {
    const num = Number(val) || 0;
    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  return {
    /** value → contract-scaled currency string (used by KPI cards / metrics tiles) */
    formatCurrencyValue: formatRaw,
    /** value already scaled → currency string with sign (used by tooltips) */
    formatCurrencyFull: formatScaledOnly,
    /** value already scaled → compact axis label */
    formatAxisCurrency: formatAxis,
  };
}

/** Build the cumulative chart series the StrategyCumulativeChart expects. */
export function buildChartData({ weeklyData, contractType = 'NQ', contractSize = 1, sheetName = '' }) {
  const isMicro = isMicroSheet(sheetName);
  const scale = (val) => {
    let num = (val ?? 0) * contractSize;
    if (contractType === 'MNQ' && !isMicro) num /= 10;
    return num;
  };

  const dailyMap = buildDailyDataMap(weeklyData);
  const entries = Array.from(dailyMap.entries())
    .map(([date, { value }]) => ({ date, scaledValue: scale(value) }))
    .filter((e) => e.date && !Number.isNaN(e.scaledValue))
    .sort((a, b) => a.date.localeCompare(b.date));

  let cumulative = 0;
  return entries.map(({ date, scaledValue }) => {
    cumulative += scaledValue;
    const [yyyy, mm, dd] = date.split('-');
    return {
      date,
      dateLabel: `${mm}/${dd}/${String(yyyy).slice(-2)}`,
      tooltipDate: `${mm}/${dd}/${yyyy}`,
      cumulative,
      dailyPnl: scaledValue,
    };
  });
}

/** Calendar month stats: { total, daysWithData } for the visible calendar month. */
export function buildCalendarMonthStats(dailyData, calendarMonth) {
  if (!dailyData || !calendarMonth) return { total: 0, daysWithData: 0 };
  const { year, month } = calendarMonth;
  const expectedMonth = month + 1;
  let total = 0;
  let daysWithData = 0;
  dailyData.forEach(({ value }, key) => {
    const [y, m] = key.split('-').map(Number);
    if (y === year && m === expectedMonth) {
      total += value;
      daysWithData += 1;
    }
  });
  return { total, daysWithData };
}

/** Year stats: 12 entries (Jan..Dec) of { monthIndex, total, daysWithData } for the visible year. */
export function buildYearStats(dailyData, calendarMonth) {
  const byMonth = Array.from({ length: 12 }, (_, i) => ({ monthIndex: i, total: 0, daysWithData: 0 }));
  if (!dailyData || !calendarMonth) return byMonth;
  const { year } = calendarMonth;
  dailyData.forEach(({ value }, key) => {
    const [y, m] = key.split('-').map(Number);
    if (y === year && m >= 1 && m <= 12) {
      byMonth[m - 1].total += value;
      byMonth[m - 1].daysWithData += 1;
    }
  });
  return byMonth;
}

/** Per-week summary rows for the calendar sidebar. Mirrors StrategyDetailsView's monthWeeksSummary. */
export function buildMonthWeeksSummary({ weeklyData, calendarMonth }) {
  if (!calendarMonth) return [];
  const { year, month } = calendarMonth;
  const weekByMonday = new Map();
  (weeklyData || []).forEach((week) => {
    if (!week?.date) return;
    if (!weekByMonday.has(week.date)) weekByMonday.set(week.date, week);
  });
  const mondayKeys = mondayKeysOverlappingMonth(year, month);
  return mondayKeys.map((mondayKey) => {
    const week = weekByMonday.get(mondayKey) || {
      date: mondayKey,
      days: { ...EMPTY_WEEK_DAYS },
      total: 0,
    };
    const daysWithData = DAY_ORDER.filter((d) => (week.days?.[d] ?? 0) !== 0).length;
    return {
      label: formatWeekLabel(week.date),
      total: week.total,
      daysCount: daysWithData,
      touchesRollWeek: false,
      touchesHighImpact: false,
      mondayDate: week.date,
      days: week.days,
    };
  });
}

/** Convenience: pull every derivation a calendar widget needs from raw rows. */
export function buildCalendarPropsFromRawRows({ rawRows, calendarMonth }) {
  const weeklyData = extractWeeklyData(rawRows);
  const dailyData = buildDailyDataMap(weeklyData);
  return {
    dailyData,
    monthWeeksSummary: buildMonthWeeksSummary({ weeklyData, calendarMonth }),
    calendarMonthStats: buildCalendarMonthStats(dailyData, calendarMonth),
    yearStats: buildYearStats(dailyData, calendarMonth),
  };
}
